import { useCallback, useEffect, useState } from "react";

export type OutboxItem = { voidId: string; channelId: string; content: string; tempId: string; attachments: { url: string; contentType?: string; name?: string; size?: number }[]; spoiler?: boolean; replyToId?: string | null };

/**
 * Offline send outbox (step 6.3a).
 *
 * Owns the queue of unsent messages: the `outbox` array (+ its localStorage
 * load/persist) and `emitOrQueue`, which appends a payload (deduped by tempId)
 * and fires `message:send` over the socket.
 *
 * The drop-on-ack (in the `onNew` message handler) and the reconnect flush (in
 * the `onConnect` handler) stay in the composition layer's shared socket effect
 * and call the returned `setOutbox` — those handlers are part of the message/
 * socket spine deferred to Phase 7 with the channels core (5.1c).
 */
export function useOutbox(socket: any) {
  // Offline outbox: queue unsent messages and flush on reconnect
  const [outbox, setOutbox] = useState<OutboxItem[]>(() => {
    try { const s = localStorage.getItem('outbox'); return s ? JSON.parse(s) : []; } catch { return []; }
  });
  useEffect(() => { try { localStorage.setItem('outbox', JSON.stringify(outbox)); } catch { /* noop */ } }, [outbox]);

  const emitOrQueue = useCallback((payload: OutboxItem) => {
    // Deduplicate same tempId in queue
    setOutbox(prev => {
      const filtered = payload.tempId ? prev.filter(it => it.tempId !== payload.tempId) : prev;
      return [...filtered, payload];
    });
    try { socket.emit("message:send", payload); } catch { /* noop */ }
  }, [socket]);

  return { outbox, setOutbox, emitOrQueue };
}
